import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import { db, app } from "../../configs/Firebase";
import { getStorage, getDownloadURL, ref as storageRef, uploadBytes, deleteObject } from "firebase/storage";
import {
  onValue,
  ref,
  push,
  remove,
  update,
  serverTimestamp,
  onDisconnect,
  set,
  get,
  off,
} from "firebase/database";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import style from "./Journey.module.css";
import Row from "./parts/Row";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Dialog from "../../designSystem/c-organism/dialogs/Dialog";
import Card from "../../designSystem/b-molecules/cards/Card";
import Dropdown from "../../designSystem/c-organism/dialogs/Dropdown";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import Tabs from "../../designSystem/b-molecules/tabs/Tabs";
import ContentEditable from "react-contenteditable";
import Input from "../../designSystem/b-molecules/formElements/input/Input";
import InputFile from "../../designSystem/b-molecules/formElements/input/InputFile";
import Checkbox from "../../designSystem/b-molecules/formElements/checkbox/Checkbox";
import Papa from "papaparse";
import LinkPersonasDialog from "./components/LinkPersonasDialog";
import JourneyOwnersDialog from "./components/JourneyOwnersDialog";
import CellCommentsSidebar from "./components/CellCommentsSidebar";
import { useNotifications } from "../../contexts/NotificationsContext";
import { calculateJourneyScore, calculateStepScoreFinal } from "./utils/scoringUtils";
import { useTimeMachine } from "../../contexts/TimeMachineContext";
import html2canvas from "html2canvas";
import { canEditSection, SECTIONS } from "../../utils/permissions";
import VideoPlayerDialog from "./components/VideoPlayerDialog";

const Journey = (data) => {
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const [user, setUser] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [journey, setJourney] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [actions, setActions] = useState([]);
  const [journeyRefURL, setJourneyRefURL] = useState(false);
  const journeySnapshotRef = useRef(null);
  
  // Time machine state
  const { timeMachineMode, setTimeMachineMode } = useTimeMachine();
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedVersionData, setSelectedVersionData] = useState(null);
  const originalJourneyRef = useRef(null);
  
  // Edit lock state
  const [editLock, setEditLock] = useState(null);
  const [isLockedByOtherUser, setIsLockedByOtherUser] = useState(false);
  const editLockRef = useRef(null);
  const editLockDisconnectRef = useRef(null);
  
  // Activity tracking refs
  const activityUpdateIntervalRef = useRef(null);
  const activityUpdateTimeoutRef = useRef(null);
  const lastActivityUpdateRef = useRef(null);

  // Video state
  const storage = getStorage(app);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const videoInputRef = useRef(null);

  useEffect(() => {
    // Show Exit Time Machine button when in time machine mode
    if (timeMachineMode) {
      setActions([
        {
          icon: "more_vert",
          name: "",
          action: showJourneyOptionsHandler,
          variants: ["text"],
        },
        { icon: "history", name: "Exit Time Machine", action: exitTimeMachine },
      ]);
      return;
    }

    if (canEdit) {
      if (editMode) {
        setActions([
          {
            icon: "more_vert",
            name: "",
            action: showJourneyOptionsHandler,
            variants: ["text"],
          },
          { icon: "close", name: "", action: editModeHandler },
        ]);
      } else {
        const editButtonDisabled = isLockedByOtherUser;
        setActions([
          {
            icon: "more_vert",
            name: "",
            action: showJourneyOptionsHandler,
            variants: ["text"],
          },
          { 
            icon: editButtonDisabled ? "lock" : "edit", 
            name: editButtonDisabled ? `Locked by ${editLock?.userName || 'another user'}` : "Edit", 
            action: editButtonDisabled ? () => {} : editModeHandler,
          },
        ]);
      }
    } else {
      setActions([
        {
          icon: "more_vert",
          name: "",
          action: showJourneyOptionsHandler,
          variants: ["text"],
        },
      ]);
    }
  }, [canEdit, editMode, timeMachineMode]);

  const prevEditModeRef = useRef(editMode);

  // Check if user can enter edit mode
  function canEnterEditMode() {
    if (!editLock) return true; // No lock exists
    if (editLock.userId === user.uid) return true; // Current user has lock
    return false; // Another user has the lock
  }

  // Acquire edit lock
  async function acquireEditLock() {
    if (!journeyRefURL || !user) return false;

    try {
      const lockRef = ref(db, journeyRefURL + "/editLock");
      
      // Check if lock exists and is held by another user
      const lockSnapshot = await get(lockRef);
      if (lockSnapshot.exists()) {
        const lockData = lockSnapshot.val();
        if (lockData.userId !== user.uid) {
          // Another user has the lock
          return false;
        }
      }

      // Set the lock
      await set(lockRef, {
        userId: user.uid,
        userName: user.displayName,
        timestamp: Date.now(),
      });

      // Set up disconnect handler to release lock automatically
      // Use onValue to monitor connection status, similar to presence system
      const connectedRef = ref(db, ".info/connected");
      const unsubscribeConnected = onValue(connectedRef, (snapshot) => {
        if (snapshot.val()) {
          // We're connected, set up disconnect handler
          if (!editLockDisconnectRef.current) {
            editLockDisconnectRef.current = onDisconnect(lockRef);
            editLockDisconnectRef.current.remove();
          }
        }
      });
      
      // Store unsubscribe function for cleanup (optional, but good practice)
      // Note: We don't need to store this as the listener will persist until component unmounts

      return true;
    } catch (error) {
      console.error("Error acquiring edit lock:", error);
      return false;
    }
  }

  // Release edit lock
  async function releaseEditLock() {
    if (!journeyRefURL) return;

    try {
      // Cancel disconnect handler first
      if (editLockDisconnectRef.current) {
        editLockDisconnectRef.current.cancel();
        editLockDisconnectRef.current = null;
      }

      // Construct lock ref if editLockRef.current is null
      const lockRef = editLockRef.current || ref(db, journeyRefURL + "/editLock");

      // Remove the lock
      await remove(lockRef);
    } catch (error) {
      console.error("Error releasing edit lock:", error);
    }
  }

  // Cleanup: release lock on component unmount or when leaving page
  useEffect(() => {
    return () => {
      // Check if the current user actually has the lock
      if (editLock && editLock.userId === user?.uid && journeyRefURL) {
        // Cancel disconnect handler
        if (editLockDisconnectRef.current) {
          editLockDisconnectRef.current.cancel();
          editLockDisconnectRef.current = null;
        }
        
        // Construct lock ref if editLockRef.current is null
        const lockRef = editLockRef.current || ref(db, journeyRefURL + "/editLock");
        
        // Remove the lock
        remove(lockRef).catch((error) => {
          console.error("Error releasing lock on unmount:", error);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLock, user, journeyRefURL]);

  async function editModeHandler() {
    // If trying to enter edit mode
    if (!editMode) {
      // Check if locked by another user
      if (isLockedByOtherUser) {
        alert(`This journey is being edited by ${editLock.userName}. Please wait until they finish editing.`);
        return;
      }

      // Try to acquire lock
      const lockAcquired = await acquireEditLock();
      if (!lockAcquired) {
        alert("Unable to acquire edit lock. Another user may be editing this journey.");
        return;
      }

      // Enter edit mode
      setEditMode(true);
    } else {
      // Exiting edit mode - release lock
      await releaseEditLock();
      setEditMode(false);
    }
  }

  // Handle automatic versioning when entering/exiting edit mode
  useEffect(() => {
    if (!journey || !user) {
      prevEditModeRef.current = editMode;
      return;
    }

    const wasInEditMode = prevEditModeRef.current;
    const isInEditMode = editMode;

    // Entering edit mode - capture snapshot
    if (!wasInEditMode && isInEditMode) {
      journeySnapshotRef.current = deepCloneJourney(journey);
    }

    // Exiting edit mode - check for changes and archive if needed, then release lock
    if (wasInEditMode && !isInEditMode && journeySnapshotRef.current) {
      if (hasJourneyChanged(journey, journeySnapshotRef.current)) {
        archiveJourneyVersion(journeySnapshotRef.current);
      }
      journeySnapshotRef.current = null;
      // Release edit lock when exiting edit mode
      releaseEditLock();
    }

    prevEditModeRef.current = editMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  const [showJourneyOptions, setShowJourneyOptions] = useState(false);
  const [journeyOptionsStyle, setjourneyOptionsStyle] = useState();
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedRowsForExport, setSelectedRowsForExport] = useState(new Set());
  const [pdfFriendly, setPdfFriendly] = useState(false);
  const [pdfMaxColumns, setPdfMaxColumns] = useState(4);
  const [hideRowTitles, setHideRowTitles] = useState(false);

  function showJourneyOptionsHandler(e) {
    setShowJourneyOptions(true);
    setjourneyOptionsStyle({
      left: "auto",
      right: window.innerWidth - e.currentTarget.getBoundingClientRect().right,
      top: e.currentTarget.getBoundingClientRect().top,
    });
  }
  function closeJourneyOptionsHandler(e) {
    setShowJourneyOptions(false);
  }

  function showExportDialogHandler() {
    if (journey && journey.rows) {
      setSelectedRowsForExport(new Set(journey.rows.map(row => row.id)));
    }
    setShowExportDialog(true);
    closeJourneyOptionsHandler();
  }

  function closeExportDialogHandler() {
    setShowExportDialog(false);
  }

  function toggleRowSelection(rowId) {
    setSelectedRowsForExport((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }

  function handleExportConfirm() {
    if (selectedRowsForExport.size === 0) {
      alert("Please select at least one row to export.");
      return;
    }
    closeExportDialogHandler();
    exportJourneyAsImage(selectedRowsForExport, pdfFriendly ? pdfMaxColumns : null, hideRowTitles);
  }

  // Helper function to convert blob to data URL
  function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Helper function to convert images to base64 data URLs using canvas
  async function convertImagesToDataURLs(element) {
    const images = element.querySelectorAll("img");
    const conversions = Array.from(images).map(async (img) => {
      // Skip if already a data URL or empty
      if (!img.src || img.src.startsWith("data:")) {
        return;
      }
      
      const originalSrc = img.src;
      
      try {
        // Create a new Image object with crossOrigin set to avoid tainted canvas
        const newImg = new Image();
        newImg.crossOrigin = "anonymous";
        
        // Load the image from the same URL with CORS
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Image load timeout"));
          }, 10000);
          
          newImg.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          
          newImg.onerror = (error) => {
            clearTimeout(timeout);
            reject(error);
          };
          
          // Set src after setting crossOrigin
          newImg.src = originalSrc;
        });
        
        // Create canvas with same dimensions as image
        const canvas = document.createElement("canvas");
        canvas.width = newImg.naturalWidth || newImg.width;
        canvas.height = newImg.naturalHeight || newImg.height;
        
        // Draw the new image (with crossOrigin) to canvas
        const ctx = canvas.getContext("2d");
        ctx.drawImage(newImg, 0, 0);
        
        // Convert canvas to data URL (should work now since image has crossOrigin)
        const dataURL = canvas.toDataURL("image/png");
        
        // Store original src for restoration
        img.dataset.originalSrc = originalSrc;
        // Replace with data URL
        img.src = dataURL;
      } catch (error) {
        console.warn("Failed to convert image via canvas:", originalSrc, error);
        // Continue with original URL - don't block export
      }
    });
    
    await Promise.all(conversions);
  }

  // Helper function to restore original image sources
  function restoreImageSources(element) {
    const images = element.querySelectorAll("img");
    images.forEach((img) => {
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        delete img.dataset.originalSrc;
      }
    });
  }

  // Helper function to wait for all images to load
  function waitForImagesToLoad(element) {
    const images = element.querySelectorAll("img");
    const imagePromises = Array.from(images).map((img) => {
      // If image is already loaded, return resolved promise
      if (img.complete && img.naturalHeight !== 0) {
        return Promise.resolve();
      }
      
      // Otherwise, wait for load or error event
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          // Timeout after 10 seconds - resolve anyway to not block export
          console.warn("Image load timeout:", img.src);
          resolve();
        }, 10000);
        
        img.addEventListener("load", () => {
          clearTimeout(timeout);
          resolve();
        });
        
        img.addEventListener("error", () => {
          clearTimeout(timeout);
          // Resolve even on error so export can continue
          resolve();
        });
      });
    });
    
    return Promise.all(imagePromises);
  }

  function trimCanvasBottom(sourceCanvas, bgColor) {
    const ctx = sourceCanvas.getContext('2d', { willReadFrequently: true });
    const { width, height } = sourceCanvas;

    const tmp = document.createElement('canvas');
    tmp.width = 1; tmp.height = 1;
    const tctx = tmp.getContext('2d');
    tctx.fillStyle = bgColor;
    tctx.fillRect(0, 0, 1, 1);
    const bgData = tctx.getImageData(0, 0, 1, 1).data;
    const bgR = bgData[0], bgG = bgData[1], bgB = bgData[2];

    const chunkSize = 100;
    const sampleStep = Math.max(1, Math.floor(width / 40));
    let bottomY = height;

    outer:
    for (let chunkBottom = height; chunkBottom > 0; chunkBottom -= chunkSize) {
      const chunkTop = Math.max(0, chunkBottom - chunkSize);
      const chunkH = chunkBottom - chunkTop;
      const data = ctx.getImageData(0, chunkTop, width, chunkH).data;

      for (let row = chunkH - 1; row >= 0; row--) {
        for (let x = 0; x < width; x += sampleStep) {
          const i = (row * width + x) * 4;
          if (Math.abs(data[i] - bgR) > 10 || Math.abs(data[i+1] - bgG) > 10 || Math.abs(data[i+2] - bgB) > 10) {
            bottomY = chunkTop + row + 1;
            break outer;
          }
        }
      }
    }

    const padding = 40;
    const trimmedHeight = Math.min(bottomY + padding, height);

    if (trimmedHeight >= height - 50) return sourceCanvas;

    const trimmed = document.createElement('canvas');
    trimmed.width = width;
    trimmed.height = trimmedHeight;
    trimmed.getContext('2d').drawImage(sourceCanvas, 0, 0, width, trimmedHeight, 0, 0, width, trimmedHeight);
    return trimmed;
  }

  async function exportJourneyAsImage(selectedRowIds = null, wrapAfterColumns = null, hideRowTitles = false) {
    if (!map.current) {
      console.error("Map element not found");
      return;
    }

    const mapElement = map.current;

    const versionHistoryElement = mapElement.querySelector(`.${style.versionHistory}`);
    let originalVersionHistoryDisplay = null;
    if (versionHistoryElement) {
      originalVersionHistoryDisplay = versionHistoryElement.style.display;
      versionHistoryElement.style.display = "none";
    }

    const rowElements = mapElement.querySelectorAll('[data-row="true"]');
    const originalRowDisplays = new Map();

    if (selectedRowIds && selectedRowIds.size > 0) {
      rowElements.forEach((rowEl) => {
        const rowId = rowEl.dataset.id;
        if (rowId) {
          originalRowDisplays.set(rowId, rowEl.style.display);
          if (!selectedRowIds.has(rowId)) {
            rowEl.style.display = "none";
          }
        }
      });
    }

    const originalOverflow = mapElement.style.overflow;
    const originalHeight = mapElement.style.height;
    const originalMinHeight = mapElement.style.minHeight;
    const originalBackgroundColor = mapElement.style.backgroundColor;
    const originalScrollTop = mapElement.scrollTop;
    const originalScrollLeft = mapElement.scrollLeft;

    try {
      mapElement.style.overflow = "visible";
      mapElement.style.height = "auto";
      await sleep(100);

      const backgroundColor = '#ffffff';

      await waitForImagesToLoad(mapElement);
      await sleep(200);

      let canvas;

      if (wrapAfterColumns && wrapAfterColumns > 0) {
        const cellSize = 240;
        const nameColumnWidth = hideRowTitles ? 0 : 162;
        const bandPixelWidth = nameColumnWidth + (wrapAfterColumns * cellSize);
        const fullHeight = mapElement.scrollHeight;

        let maxColsInDom = 0;
        const visRowEls = Array.from(mapElement.querySelectorAll('[data-row="true"]'))
          .filter(r => r.style.display !== 'none');
        visRowEls.forEach(row => {
          const cells = Array.from(row.querySelectorAll('[data-cell="true"]'));
          let rowCols = 0;
          cells.forEach(cell => {
            rowCols += Math.round(parseFloat(cell.style.getPropertyValue('--span')) || 1);
          });
          maxColsInDom = Math.max(maxColsInDom, rowCols);
        });

        const numBands = Math.ceil(maxColsInDom / wrapAfterColumns);

        mapElement.style.backgroundColor = backgroundColor;
        mapElement.style.minHeight = `${fullHeight}px`;

        // Capture each band separately so html2canvas handles its own image
        // loading on the original cloned elements (no double-cloning).
        const bandCanvases = [];

        for (let bandIdx = 0; bandIdx < numBands; bandIdx++) {
          const bandStart = bandIdx * wrapAfterColumns;
          const bandEnd = (bandIdx + 1) * wrapAfterColumns;

          let bandImageRects = [];
          let cloneMapWidth = bandPixelWidth;

          const bandCanvas = await html2canvas(mapElement, {
            width: bandPixelWidth,
            height: fullHeight,
            scrollX: 0,
            scrollY: 0,
            useCORS: true,
            allowTaint: true,
            scale: 4,
            logging: false,
            backgroundColor: backgroundColor,
            onclone: (clonedDoc) => {
              const clonedMap = clonedDoc.querySelector(`.${style.map}`);
              if (!clonedMap) return;

              const hideHoverStyle = clonedDoc.createElement('style');
              hideHoverStyle.textContent = `
                [title="View comments"],
                [title="Delete cell"],
                [title="Delete row"],
                [aria-label="Add cell to the left"],
                [aria-label="Add cell to the right"],
                [aria-label="Add row above"],
                [aria-label="Add row below"] {
                  display: none !important;
                }
              `;
              clonedDoc.head.appendChild(hideHoverStyle);

              clonedMap.style.backgroundColor = backgroundColor;
              clonedMap.style.width = `${bandPixelWidth}px`;
              clonedMap.style.maxWidth = 'none';
              clonedMap.style.overflow = 'visible';
              clonedMap.style.height = 'auto';
              clonedMap.style.minHeight = '0';

              const vhClone = clonedMap.querySelector(`.${style.versionHistory}`);
              if (vhClone) vhClone.remove();

              const rows = Array.from(clonedMap.querySelectorAll('[data-row="true"]'));
              rows.filter(r => r.style.display !== 'none').forEach(row => {
                for (const child of row.children) {
                  if (child.tagName === 'DIV' && !child.hasAttribute('data-cell')) {
                    if (hideRowTitles) {
                      child.style.display = 'none';
                    } else {
                      child.style.position = 'relative';
                      const inner = child.firstElementChild;
                      if (inner) {
                        inner.style.position = 'relative';
                        inner.style.top = '0';
                      }
                    }
                    break;
                  }
                }

                const cells = Array.from(row.querySelectorAll('[data-cell="true"]'));
                let colUnit = 0;
                cells.forEach(cell => {
                  const span = Math.round(parseFloat(cell.style.getPropertyValue('--span')) || 1);
                  const cellStart = colUnit;
                  const cellEnd = colUnit + span;
                  colUnit = cellEnd;

                  if (cellEnd <= bandStart || cellStart >= bandEnd) {
                    cell.style.display = 'none';
                  } else {
                    const visibleStart = Math.max(cellStart, bandStart);
                    const visibleEnd = Math.min(cellEnd, bandEnd);
                    const visibleSpan = visibleEnd - visibleStart;
                    if (visibleSpan !== span) {
                      cell.style.setProperty('--span', visibleSpan);
                    }
                  }
                });
              });

              const mapRect = clonedMap.getBoundingClientRect();
              cloneMapWidth = mapRect.width;
              const imgs = clonedMap.querySelectorAll('img');
              imgs.forEach(img => {
                const r = img.getBoundingClientRect();
                if (r.width > 0 && r.height > 0) {
                  bandImageRects.push({
                    x: r.left - mapRect.left,
                    y: r.top - mapRect.top,
                    w: r.width,
                    h: r.height,
                  });
                }
              });
            },
          });

          if (bandImageRects.length > 0) {
            const bCtx = bandCanvas.getContext('2d');
            bCtx.setTransform(1, 0, 0, 1, 0, 0);
            const cs = bandCanvas.width / cloneMapWidth;
            bandImageRects.forEach(rect => {
              const x = Math.round(rect.x * cs);
              const y = Math.round(rect.y * cs);
              const w = Math.round(rect.w * cs);
              const h = Math.round(rect.h * cs);
              const rad = Math.round(10 * cs);
              if (w <= 0 || h <= 0 || x < 0 || y < 0 || x + w > bandCanvas.width || y + h > bandCanvas.height) return;
              const imgData = bCtx.getImageData(x, y, w, h);
              const tmp = document.createElement('canvas');
              tmp.width = w;
              tmp.height = h;
              tmp.getContext('2d').putImageData(imgData, 0, 0);

              bCtx.save();
              bCtx.shadowColor = 'rgba(0, 0, 0, 0.25)';
              bCtx.shadowBlur = 16 * cs;
              bCtx.shadowOffsetX = 0;
              bCtx.shadowOffsetY = 4 * cs;
              bCtx.fillStyle = '#ffffff';
              bCtx.beginPath();
              bCtx.roundRect(x, y, w, h, rad);
              bCtx.fill();
              bCtx.restore();

              bCtx.save();
              bCtx.beginPath();
              bCtx.roundRect(x, y, w, h, rad);
              bCtx.clip();
              bCtx.drawImage(tmp, x, y);
              bCtx.restore();
            });
          }

          bandCanvases.push(trimCanvasBottom(bandCanvas, backgroundColor));
        }

        // Stitch band canvases together with connector lines
        const scaleFactor = 4;
        const connectorHeight = 60;
        const connectorPx = connectorHeight * scaleFactor;
        const totalHeight = bandCanvases.reduce((sum, c) => sum + c.height, 0)
          + (numBands - 1) * connectorPx;
        const totalWidth = bandCanvases[0].width;

        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = totalWidth;
        finalCanvas.height = totalHeight;
        const ctx = finalCanvas.getContext('2d');

        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, totalWidth, totalHeight);

        let yOffset = 0;
        for (let i = 0; i < bandCanvases.length; i++) {
          if (i > 0) {
            const lastCellCenterX = (nameColumnWidth + (wrapAfterColumns - 0.5) * cellSize) * scaleFactor;
            const firstCellCenterX = (nameColumnWidth + 0.5 * cellSize) * scaleFactor;
            const vPad = 10 * scaleFactor;

            ctx.strokeStyle = '#999';
            ctx.lineWidth = 2 * scaleFactor;
            ctx.beginPath();
            const midY = yOffset + connectorPx / 2;
            const radius = 10 * scaleFactor;
            ctx.moveTo(lastCellCenterX, yOffset);
            ctx.arcTo(lastCellCenterX, midY, firstCellCenterX, midY, radius);
            ctx.arcTo(firstCellCenterX, midY, firstCellCenterX, yOffset + connectorPx, radius);
            ctx.lineTo(firstCellCenterX, yOffset + connectorPx);
            ctx.stroke();

            const arrowSize = 8 * scaleFactor;
            ctx.fillStyle = '#999';
            ctx.beginPath();
            ctx.moveTo(firstCellCenterX, yOffset + connectorPx);
            ctx.lineTo(firstCellCenterX - arrowSize / 2, yOffset + connectorPx - arrowSize);
            ctx.lineTo(firstCellCenterX + arrowSize / 2, yOffset + connectorPx - arrowSize);
            ctx.closePath();
            ctx.fill();

            yOffset += connectorPx;
          }

          ctx.drawImage(bandCanvases[i], 0, yOffset);
          yOffset += bandCanvases[i].height;
        }

        canvas = finalCanvas;
      } else {
        const fullWidth = mapElement.scrollWidth;
        const fullHeight = mapElement.scrollHeight;
        mapElement.style.backgroundColor = backgroundColor;
        mapElement.style.minHeight = `${fullHeight}px`;

        let regularImageRects = [];
        let cloneMapWidth = fullWidth;

        canvas = await html2canvas(mapElement, {
          width: fullWidth,
          height: fullHeight,
          scrollX: 0,
          scrollY: 0,
          useCORS: true,
          allowTaint: true,
          scale: 4,
          logging: false,
          backgroundColor: backgroundColor,
          onclone: (clonedDoc) => {
            const hideHoverStyle = clonedDoc.createElement('style');
            hideHoverStyle.textContent = `
              [title="View comments"],
              [title="Delete cell"],
              [title="Delete row"],
              [aria-label="Add cell to the left"],
              [aria-label="Add cell to the right"],
              [aria-label="Add row above"],
              [aria-label="Add row below"] {
                display: none !important;
              }
            `;
            clonedDoc.head.appendChild(hideHoverStyle);

            const clonedMap = clonedDoc.querySelector(`.${style.map}`);
            if (clonedMap) {
              clonedMap.style.backgroundColor = backgroundColor;
              clonedMap.style.minHeight = `${fullHeight}px`;

              if (hideRowTitles) {
                const rows = Array.from(clonedMap.querySelectorAll('[data-row="true"]'));
                rows.forEach(row => {
                  for (const child of row.children) {
                    if (child.tagName === 'DIV' && !child.hasAttribute('data-cell')) {
                      child.style.display = 'none';
                      break;
                    }
                  }
                });
              }

              const mapRect = clonedMap.getBoundingClientRect();
              cloneMapWidth = mapRect.width;
              const imgs = clonedMap.querySelectorAll('img');
              imgs.forEach(img => {
                const r = img.getBoundingClientRect();
                if (r.width > 0 && r.height > 0) {
                  regularImageRects.push({
                    x: r.left - mapRect.left,
                    y: r.top - mapRect.top,
                    w: r.width,
                    h: r.height,
                  });
                }
              });
            }
          },
        });

        if (regularImageRects.length > 0) {
          const rCtx = canvas.getContext('2d');
          rCtx.setTransform(1, 0, 0, 1, 0, 0);
          const cs = canvas.width / cloneMapWidth;
          regularImageRects.forEach(rect => {
            const x = Math.round(rect.x * cs);
            const y = Math.round(rect.y * cs);
            const w = Math.round(rect.w * cs);
            const h = Math.round(rect.h * cs);
            const rad = Math.round(10 * cs);
            if (w <= 0 || h <= 0 || x < 0 || y < 0 || x + w > canvas.width || y + h > canvas.height) return;
            const imgData = rCtx.getImageData(x, y, w, h);
            const tmp = document.createElement('canvas');
            tmp.width = w;
            tmp.height = h;
            tmp.getContext('2d').putImageData(imgData, 0, 0);

            rCtx.save();
            rCtx.shadowColor = 'rgba(0, 0, 0, 0.25)';
            rCtx.shadowBlur = 16 * cs;
            rCtx.shadowOffsetX = 0;
            rCtx.shadowOffsetY = 4 * cs;
            rCtx.fillStyle = '#ffffff';
            rCtx.beginPath();
            rCtx.roundRect(x, y, w, h, rad);
            rCtx.fill();
            rCtx.restore();

            rCtx.save();
            rCtx.beginPath();
            rCtx.roundRect(x, y, w, h, rad);
            rCtx.clip();
            rCtx.drawImage(tmp, x, y);
            rCtx.restore();
          });
        }
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          console.error("Failed to create blob from canvas");
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const journeyName = journey?.name || "journey";
        const sanitizedName = journeyName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
        link.download = `${sanitizedName}-export.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (error) {
      console.error("Error exporting journey as image:", error);
      alert("Failed to export journey. Please try again.");
    } finally {
      rowElements.forEach((rowEl) => {
        const rowId = rowEl.dataset.id;
        if (rowId && originalRowDisplays.has(rowId)) {
          rowEl.style.display = originalRowDisplays.get(rowId) || "";
        }
      });

      mapElement.style.overflow = originalOverflow;
      mapElement.style.height = originalHeight;
      mapElement.style.minHeight = originalMinHeight;
      mapElement.style.backgroundColor = originalBackgroundColor;
      mapElement.scrollTop = originalScrollTop;
      mapElement.scrollLeft = originalScrollLeft;

      if (versionHistoryElement) {
        versionHistoryElement.style.display = originalVersionHistoryDisplay || "";
      }
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const [hasNoData, setHasNoData] = useState(false);
  const [maxColumns, setMaxColumns] = useState(0);
  const [brands, setBrands] = useState([]);
  const [journeyScore, setJourneyScore] = useState(null);

  const [users, setUsers] = useState([]);

  function addZero(i) {
    if (i < 10) {i = "0" + i}
    return i;
  }

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      setJourneyRefURL("userData/" + org.payload + "/journeys/" + params.id);
      const journeyRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id
      );

      return onValue(journeyRef, (snapshot) => {
        // console.log("journey changed", snapshot.val());
        let data = snapshot.val();
        let tempRows = [];
        let tempMaxColumns = 0;

        for (let i in data.rows) {
          // Convert columns object to array if it exists
          if (data.rows[i].columns) {
            // Check if it's an object (Firebase stores arrays as objects)
            if (!Array.isArray(data.rows[i].columns)) {
              let columnsArray = [];
              for (let colKey in data.rows[i].columns) {
                columnsArray.push({
                  ...data.rows[i].columns[colKey],
                  // Use Firebase key as ID, but preserve existing id if it's more specific
                  id: data.rows[i].columns[colKey].id || colKey
                });
              }
              // Sort by displayOrder if available
              columnsArray.sort((a, b) => {
                const orderA = a.displayOrder !== undefined ? a.displayOrder : parseInt(a.id);
                const orderB = b.displayOrder !== undefined ? b.displayOrder : parseInt(b.id);
                return orderA - orderB;
              });
              data.rows[i].columns = columnsArray;
            }
            if (data.rows[i].columns.length > tempMaxColumns) {
              tempMaxColumns = data.rows[i].columns.length;
            }
          }
          tempRows.push({
            id: i,
            ...data.rows[i],
          });
        }

        setMaxColumns(tempMaxColumns);

        data.rows = tempRows;

        if (!data.rows.length) {
          setHasNoData(true);
        } else {
          setHasNoData(false);
        }

        data.rows.sort(function (a, b) {
          return a.displayOrder - b.displayOrder;
        });

        // Ensure brandIds exists
        if (!data.brandIds) {
          data.brandIds = [];
        }

        sleep(1).then(() => {
          setJourney(data);
        });

        let tempArchive = [];
        for (let i in data.archive) {
          const timestamp = data.archive[i].timestamp;
          const date = new Date(timestamp);
          const datevalues = [
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
            addZero(date.getHours()),
            addZero(date.getMinutes()),
            addZero(date.getSeconds()),
          ];

          // Store original timestamp for sorting
          const timestampOrig = timestamp;

          data.archive[i].timestamp = (
            <div className={style.archiveDate}>
              <div>
                {datevalues[3]}:{datevalues[4]}:{datevalues[5]}
              </div>
              <div>
                {datevalues[2]} / {datevalues[1]} / {datevalues[0]}
              </div>
            </div>
          );
          tempArchive.push({
            id: i,
            ...data.archive[i],
            timestampOrig: timestampOrig, // Store for sorting
          });
        }

        // Sort archive by timestamp (most recent first)
        tempArchive.sort((a, b) => {
          const timestampA = a.timestampOrig || 0;
          const timestampB = b.timestampOrig || 0;
          return timestampB - timestampA; // Descending order
        });

        data.archive = tempArchive;

        let tempNotes = [];
        for (let i in data.notes) {
          const timestamp = data.notes[i].timestamp;
          const date = new Date(timestamp);
          const datevalues = [
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
          ];
          data.notes[i].timestampOrig = data.notes[i].timestamp;
          data.notes[i].timestamp = (
            <div className={style.archiveDate}>
              <div>
                {datevalues[3]}:{datevalues[4]}:{datevalues[5]}
              </div>
              <div>
                {datevalues[2]} / {datevalues[1]} / {datevalues[0]}
              </div>
            </div>
          );
          tempNotes.push({
            id: i,
            ...data.notes[i],
          });
        }
        data.notes = tempNotes.sort(compare);
      });

      // Load brands
      const brandsRef = ref(db, `userData/${org.payload}/brands`);
      onValue(brandsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const brandsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setBrands(brandsList);
        } else {
          setBrands([]);
        }
      });
    }
  }, [org.payload, params.id]);

  function compare(a, b) {
    if (a.timestampOrig > b.timestampOrig) {
      return -1;
    }
    if (a.timestampOrig < b.timestampOrig) {
      return 1;
    }
    return 0;
  }

  // Deep clone function for journey snapshot
  function deepCloneJourney(journey) {
    if (!journey) return null;
    
    const cloned = JSON.parse(JSON.stringify(journey));
    // Ensure rows array is properly cloned
    if (cloned.rows && Array.isArray(cloned.rows)) {
      cloned.rows = cloned.rows.map(row => ({
        ...row,
        columns: row.columns ? (Array.isArray(row.columns) ? [...row.columns] : Object.values(row.columns)) : []
      }));
    }
    return cloned;
  }

  // Compare two journey objects to detect changes
  function hasJourneyChanged(currentJourney, snapshotJourney) {
    if (!currentJourney || !snapshotJourney) {
      return false;
    }

    // Compare journey name
    if (currentJourney.name !== snapshotJourney.name) {
      return true;
    }

    // Compare number of rows
    const currentRows = currentJourney.rows || [];
    const snapshotRows = snapshotJourney.rows || [];
    
    if (currentRows.length !== snapshotRows.length) {
      return true;
    }

    // Sort rows by displayOrder for consistent comparison
    const sortedCurrentRows = [...currentRows].sort((a, b) => {
      const orderA = a.displayOrder !== undefined ? a.displayOrder : 0;
      const orderB = b.displayOrder !== undefined ? b.displayOrder : 0;
      return orderA - orderB;
    });
    
    const sortedSnapshotRows = [...snapshotRows].sort((a, b) => {
      const orderA = a.displayOrder !== undefined ? a.displayOrder : 0;
      const orderB = b.displayOrder !== undefined ? b.displayOrder : 0;
      return orderA - orderB;
    });

    // Compare each row
    for (let i = 0; i < sortedCurrentRows.length; i++) {
      const currentRow = sortedCurrentRows[i];
      const snapshotRow = sortedSnapshotRows[i];

      // Compare row properties
      if (currentRow.id !== snapshotRow.id ||
          currentRow.type !== snapshotRow.type ||
          currentRow.value !== snapshotRow.value ||
          currentRow.displayOrder !== snapshotRow.displayOrder) {
        return true;
      }

      // Compare columns
      const currentColumns = currentRow.columns || [];
      const snapshotColumns = snapshotRow.columns || [];

      // Handle both array and object formats
      const currentColsArray = Array.isArray(currentColumns) 
        ? currentColumns 
        : Object.values(currentColumns);
      const snapshotColsArray = Array.isArray(snapshotColumns)
        ? snapshotColumns
        : Object.values(snapshotColumns);

      // Sort columns by displayOrder for consistent comparison
      const sortedCurrentCols = [...currentColsArray].sort((a, b) => {
        const orderA = a.displayOrder !== undefined ? a.displayOrder : 0;
        const orderB = b.displayOrder !== undefined ? b.displayOrder : 0;
        return orderA - orderB;
      });
      
      const sortedSnapshotCols = [...snapshotColsArray].sort((a, b) => {
        const orderA = a.displayOrder !== undefined ? a.displayOrder : 0;
        const orderB = b.displayOrder !== undefined ? b.displayOrder : 0;
        return orderA - orderB;
      });

      if (sortedCurrentCols.length !== sortedSnapshotCols.length) {
        return true;
      }

      // Compare each column/cell
      for (let j = 0; j < sortedCurrentCols.length; j++) {
        const currentCell = sortedCurrentCols[j];
        const snapshotCell = sortedSnapshotCols[j];

        if (!currentCell || !snapshotCell) {
          return true;
        }

        // Compare cell properties
        if (currentCell.value !== snapshotCell.value ||
            currentCell.style !== snapshotCell.style ||
            currentCell.textAlign !== snapshotCell.textAlign ||
            currentCell.span !== snapshotCell.span ||
            currentCell.displayOrder !== snapshotCell.displayOrder) {
          return true;
        }
      }
    }

    return false;
  }

  // Archive a specific journey version
  function archiveJourneyVersion(journeyToArchive) {
    if (!journeyToArchive || !user || !org.payload) {
      return;
    }

    const archiveRef = ref(
      db,
      "userData/" + org.payload + "/archive/journeys/"
    );
    
    let journeyTemp = {
      ...journeyToArchive,
    };
    
    // Remove fields that shouldn't be archived
    delete journeyTemp.archive;
    delete journeyTemp.presence;
    delete journeyTemp.notes;

    push(archiveRef, journeyTemp).then((snapshot) => {
      const key = snapshot.key;
      const journeyArchiveRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id + "/archive/"
      );
      push(journeyArchiveRef, {
        id: key,
        by: user.displayName,
        timestamp: { ".sv": "timestamp" },
      });
    });
  }

  // Process archived journey data similar to current journey processing
  function processArchivedJourneyData(data) {
    if (!data) return null;

    let tempRows = [];
    let tempMaxColumns = 0;

    if (data.rows) {
      for (let i in data.rows) {
        // Convert columns object to array if it exists
        if (data.rows[i].columns) {
          // Check if it's an object (Firebase stores arrays as objects)
          if (!Array.isArray(data.rows[i].columns)) {
            let columnsArray = [];
            for (let colKey in data.rows[i].columns) {
              columnsArray.push({
                ...data.rows[i].columns[colKey],
                id: data.rows[i].columns[colKey].id || colKey
              });
            }
            // Sort by displayOrder if available
            columnsArray.sort((a, b) => {
              const orderA = a.displayOrder !== undefined ? a.displayOrder : parseInt(a.id);
              const orderB = b.displayOrder !== undefined ? b.displayOrder : parseInt(b.id);
              return orderA - orderB;
            });
            data.rows[i].columns = columnsArray;
          }
          if (data.rows[i].columns.length > tempMaxColumns) {
            tempMaxColumns = data.rows[i].columns.length;
          }
        }
        tempRows.push({
          id: i,
          ...data.rows[i],
        });
      }
    }

    data.rows = tempRows;
    data.rows.sort(function (a, b) {
      return a.displayOrder - b.displayOrder;
    });

    // Ensure brandIds exists
    if (!data.brandIds) {
      data.brandIds = [];
    }

    return data;
  }

  // Load an archived version and enter time machine mode
  async function loadArchivedVersion(versionId) {
    if (!versionId || !org.payload) {
      return;
    }

    try {
      const archiveRef = ref(
        db,
        `userData/${org.payload}/archive/journeys/${versionId}`
      );
      const snapshot = await get(archiveRef);
      
      if (snapshot.exists()) {
        const archivedData = snapshot.val();
        
        // Store original journey if not already stored
        if (!originalJourneyRef.current) {
          originalJourneyRef.current = journey;
        }
        
        // Process archived data similar to current journey
        const processedData = processArchivedJourneyData(archivedData);
        
        if (processedData) {
          setSelectedVersionId(versionId);
          setSelectedVersionData(processedData);
          setTimeMachineMode(true);
          // Exit edit mode when entering time machine
          if (editMode) {
            setEditMode(false);
          }
        }
      }
    } catch (error) {
      console.error("Error loading archived version:", error);
    }
  }

  // Exit time machine mode and return to current version
  function exitTimeMachine() {
    setTimeMachineMode(false);
    setSelectedVersionId(null);
    setSelectedVersionData(null);
    originalJourneyRef.current = null;
  }

  // Navigate to previous version
  function navigateToPreviousVersion() {
    if (!journey.archive || journey.archive.length === 0) return;
    
    // If viewing current version, navigate to most recent archived version
    if (!timeMachineMode || !selectedVersionId) {
      const mostRecentVersion = journey.archive[journey.archive.length - 1];
      if (mostRecentVersion) {
        loadArchivedVersion(mostRecentVersion.id);
      }
      return;
    }
    
    const currentIndex = journey.archive.findIndex(v => v.id === selectedVersionId);
    if (currentIndex > 0) {
      const previousVersion = journey.archive[currentIndex - 1];
      loadArchivedVersion(previousVersion.id);
    } else if (currentIndex === 0) {
      // From the most recent archived version, go back to the current version
      setSelectedVersionId(null);
      setSelectedVersionData(null);
    }
  }

  // Navigate to next version
  function navigateToNextVersion() {
    if (!journey.archive || journey.archive.length === 0) return;
    
    const currentIndex = journey.archive.findIndex(v => v.id === selectedVersionId);
    if (currentIndex < journey.archive.length - 1) {
      const nextVersion = journey.archive[currentIndex + 1];
      loadArchivedVersion(nextVersion.id);
    } else {
      // If at the most recent archived version, navigate to current version
      // while staying in time machine mode
      setSelectedVersionId(null);
      setSelectedVersionData(null);
    }
  }

  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  // Calculate JourneyScore from all scoring cells
  useEffect(() => {
    if (!journeyRefURL || !journey || !journey.rows) {
      setJourneyScore(null);
      return;
    }

    const scoringRows = journey.rows.filter(row => row.type === "scoring");
    if (scoringRows.length === 0) {
      setJourneyScore(null);
      return;
    }

    const scoringPath = `${journeyRefURL}/scoring`;
    const scoringRef = ref(db, scoringPath);

    get(scoringRef).then((scoringSnapshot) => {
      if (scoringSnapshot.exists()) {
        const scoringData = scoringSnapshot.val();
        const stepScores = [];

        // Collect all StepScoreFinal values from scoring cells
        for (const rowId in scoringData) {
          for (const cellId in scoringData[rowId]) {
            const evaluations = scoringData[rowId][cellId].evaluations;
            if (evaluations) {
              const evaluationsList = Object.keys(evaluations).map(evalId => ({
                id: evalId,
                ...evaluations[evalId]
              }));
              const stepScoreFinal = calculateStepScoreFinal(evaluationsList);
              if (stepScoreFinal !== null) {
                stepScores.push(stepScoreFinal);
              }
            }
          }
        }

        const journeyScoreValue = calculateJourneyScore(stepScores);
        setJourneyScore(journeyScoreValue);
      } else {
        setJourneyScore(null);
      }
    }).catch(() => {
      setJourneyScore(null);
    });
  }, [journeyRefURL, journey]);

  useEffect(() => {
    if (user && org.payload) {
      setCanEdit(canEditSection(user, org.payload, SECTIONS.JOURNEYS));

      let userPresence = ref(
        db,
        "userData/" +
        org.payload +
        "/journeys/" +
        params.id +
        "/presence/" +
        user.uid
      );

      // Activity tracking for away detection
      const AWAY_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
      const ACTIVITY_UPDATE_INTERVAL = 30 * 1000; // Update every 30 seconds
      lastActivityUpdateRef.current = Date.now();

      // Function to update lastActivity timestamp
      const updateLastActivity = () => {
        const now = Date.now();
        // Only update if at least 5 seconds have passed since last update (debounce)
        if (now - (lastActivityUpdateRef.current || 0) > 5000) {
          update(userPresence, {
            lastActivity: now,
            status: "online",
          });
          lastActivityUpdateRef.current = now;
        }
      };

      // Debounced activity handler
      const handleActivity = () => {
        if (activityUpdateTimeoutRef.current) {
          clearTimeout(activityUpdateTimeoutRef.current);
        }
        activityUpdateTimeoutRef.current = setTimeout(() => {
          updateLastActivity();
        }, 2000); // Wait 2 seconds after last activity before updating
      };

      onValue(ref(db, ".info/connected"), (snapshot) => {
        if (snapshot.val()) {
          onDisconnect(userPresence).remove();
          // Fetch the user's profile to include their photo URL in presence
          get(ref(db, "userData/" + user.uid + "/profile"))
            .then((profileSnapshot) => {
              const profile = profileSnapshot.val() || {};
              const now = Date.now();
              set(userPresence, {
                status: "online",
                name: user.displayName,
                email: user.email,
                initials:
                  user.displayName.split(" ")[0].charAt(0) +
                  (user.displayName.split(" ")[1]
                    ? user.displayName.split(" ")[1].charAt(0)
                    : ""),
                photoURL: profile.microsoftPhotoURL || null,
                lastActivity: now,
              });
              lastActivityUpdateRef.current = now;

              // Set up periodic activity updates
              activityUpdateIntervalRef.current = setInterval(() => {
                updateLastActivity();
              }, ACTIVITY_UPDATE_INTERVAL);

              // Add activity event listeners
              window.addEventListener("mousemove", handleActivity);
              window.addEventListener("keydown", handleActivity);
              window.addEventListener("scroll", handleActivity, true);
            })
            .catch(() => {
              // Fallback: set presence without photo if profile fetch fails
              const now = Date.now();
              set(userPresence, {
                status: "online",
                name: user.displayName,
                email: user.email,
                initials:
                  user.displayName.split(" ")[0].charAt(0) +
                  (user.displayName.split(" ")[1]
                    ? user.displayName.split(" ")[1].charAt(0)
                    : ""),
                photoURL: null,
                lastActivity: now,
              });
              lastActivityUpdateRef.current = now;

              // Set up periodic activity updates
              activityUpdateIntervalRef.current = setInterval(() => {
                updateLastActivity();
              }, ACTIVITY_UPDATE_INTERVAL);

              // Add activity event listeners
              window.addEventListener("mousemove", handleActivity);
              window.addEventListener("keydown", handleActivity);
              window.addEventListener("scroll", handleActivity, true);
            });
        }
      });

      onValue(
        ref(
          db,
          "userData/" + org.payload + "/journeys/" + params.id + "/presence/"
        ),
        async (snapshot) => {
          let tempUsers = [];
          const presenceData = snapshot.val() || {};
          const now = Date.now();
          const AWAY_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
          
          // First, collect all online users (excluding current user)
          const usersNeedingProfiles = [];
          for (let i in presenceData) {
            const presence = presenceData[i];
            if (i !== user.uid && presence.status === "online") {
              // Check if user is away based on lastActivity
              const lastActivity = presence.lastActivity || 0;
              const isAway = (now - lastActivity) > AWAY_THRESHOLD;
              
              const userData = {
                uid: i,
                ...presence,
                photoURL: presence.photoURL || null,
                isAway: isAway,
              };
              
              // If photoURL is missing, we'll need to fetch the profile
              if (!userData.photoURL) {
                usersNeedingProfiles.push(userData);
              } else {
                tempUsers.push(userData);
              }
            }
          }

          // Fetch profiles for users missing photoURL in parallel
          if (usersNeedingProfiles.length > 0) {
            const profilePromises = usersNeedingProfiles.map(async (userData) => {
              try {
                const profileSnapshot = await get(ref(db, "userData/" + userData.uid + "/profile"));
                const profile = profileSnapshot.val() || {};
                return {
                  ...userData,
                  photoURL: profile.microsoftPhotoURL || null,
                };
              } catch (error) {
                console.error(`Error loading profile for viewer ${userData.uid}:`, error);
                // Return user data without photoURL if fetch fails
                return userData;
              }
            });

            const usersWithProfiles = await Promise.all(profilePromises);
            tempUsers = [...tempUsers, ...usersWithProfiles];
          }

          setUsers(tempUsers);
        }
      );

      return function cleanUp() {
        // Remove presence
        remove(userPresence);
        
        // Clear activity tracking
        if (activityUpdateIntervalRef.current) {
          clearInterval(activityUpdateIntervalRef.current);
          activityUpdateIntervalRef.current = null;
        }
        if (activityUpdateTimeoutRef.current) {
          clearTimeout(activityUpdateTimeoutRef.current);
          activityUpdateTimeoutRef.current = null;
        }
        
        // Remove event listeners
        window.removeEventListener("mousemove", handleActivity);
        window.removeEventListener("keydown", handleActivity);
        window.removeEventListener("scroll", handleActivity, true);
      };
    }
  }, [user, org.payload]);

  // Monitor edit lock
  useEffect(() => {
    if (!journeyRefURL || !user) {
      return;
    }

    const lockRef = ref(db, journeyRefURL + "/editLock");
    editLockRef.current = lockRef;

    const unsubscribe = onValue(lockRef, (snapshot) => {
      if (snapshot.exists()) {
        const lockData = snapshot.val();
        setEditLock(lockData);
        setIsLockedByOtherUser(lockData.userId !== user.uid);
      } else {
        setEditLock(null);
        setIsLockedByOtherUser(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [journeyRefURL, user]);

  // Load journey owners
  useEffect(() => {
    if (org.payload && params.id) {
      const ownersRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id + "/owners/"
      );

      const unsubscribe = onValue(ownersRef, async (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const ownerIds = Object.keys(data);
          const ownersWithProfiles = [];

          // Fetch profile data for each owner
          for (const ownerId of ownerIds) {
            try {
              const profileSnapshot = await get(ref(db, "userData/" + ownerId + "/profile"));
              const profile = profileSnapshot.val() || {};

              const displayName = profile.displayName || "";
              const initials = displayName
                ? displayName.split(" ")[0].charAt(0) +
                (displayName.split(" ")[1] ? displayName.split(" ")[1].charAt(0) : "")
                : "";

              ownersWithProfiles.push({
                uid: ownerId,
                name: displayName,
                email: profile.email || "",
                photoURL: profile.microsoftPhotoURL || null,
                initials: initials,
              });
            } catch (error) {
              console.error(`Error loading profile for owner ${ownerId}:`, error);
              // Add owner without profile data
              ownersWithProfiles.push({
                uid: ownerId,
                name: "",
                email: "",
                photoURL: null,
                initials: "",
              });
            }
          }

          setJourneyOwners(ownersWithProfiles);
        } else {
          setJourneyOwners([]);
        }
      });

      return () => {
        off(ownersRef);
      };
    }
  }, [org.payload, params.id]);

  // useEffect(() => {
  //   console.log("journey changed", journey);
  // }, [journey]);

  function createNewVersion() {
    const archiveRef = ref(
      db,
      "userData/" + org.payload + "/archive/journeys/"
    );
    let journeyTemp = {
      ...journey,
    };
    // deletejourneyTemp.archive = null;
    // journeyTemp.presence = null;
    delete journeyTemp.archive;
    delete journeyTemp.presence;
    delete journeyTemp.notes;

    push(archiveRef, journeyTemp).then((snapshot) => {
      const key = snapshot.key;
      const journeyArchiveRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id + "/archive/"
      );
      console.log("pushing the reference to existing version");
      push(journeyArchiveRef, {
        id: key,
        by: user.displayName,
        timestamp: { ".sv": "timestamp" },
      });
    });
  }

  function onAddLabel(label, isNew, color, id) {
    if (isNew) {
      const labelsRef = ref(db, "userData/" + org.payload + "/labels/");
      push(labelsRef, {
        value: label,
        color: color,
      });
    } else {
      const labelRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id + "/labels/" + id
      );
      update(labelRef, {
        value: label,
        color: color,
      });
    }
  }

  function onRemoveLabel(label) {
    const labelRef = ref(
      db,
      "userData/" +
      org.payload +
      "/journeys/" +
      params.id +
      "/labels/" +
      label.id
    );
    remove(labelRef);
  }

  function addRow(newRow, insertAfterRowIndex = -1) {
    if (insertAfterRowIndex === -1 || !journey.rows || journey.rows.length === 0) {
      // Append to the end
      newRow.displayOrder = journey.rows ? journey.rows.length : 0;
      const newRowRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id + "/rows/"
      );
      push(newRowRef, newRow);
    } else {
      // Insert at specific position
      // journey.rows is already sorted by displayOrder from the Firebase listener
      let newDisplayOrder;
      let toUpdate = {};

      if (insertAfterRowIndex < 0) {
        // Insert at the beginning (above first row)
        newDisplayOrder = 0;
        // Increment all existing rows
        journey.rows.forEach((row) => {
          const currentOrder = row.displayOrder !== undefined ? row.displayOrder : 0;
          toUpdate[journeyRefURL + "/rows/" + row.id + "/displayOrder"] = currentOrder + 1;
        });
      } else if (insertAfterRowIndex >= journey.rows.length - 1) {
        // Insert at the end (below last row)
        const lastRow = journey.rows[journey.rows.length - 1];
        const lastOrder = lastRow.displayOrder !== undefined ? lastRow.displayOrder : journey.rows.length - 1;
        newDisplayOrder = lastOrder + 1;
      } else {
        // Insert between rows - insert after row at insertAfterRowIndex
        const targetRow = journey.rows[insertAfterRowIndex];
        const targetOrder = targetRow.displayOrder !== undefined ? targetRow.displayOrder : insertAfterRowIndex;
        newDisplayOrder = targetOrder + 1;

        // Increment all rows that come after the insertion point
        journey.rows.forEach((row, index) => {
          if (index > insertAfterRowIndex) {
            const currentOrder = row.displayOrder !== undefined ? row.displayOrder : index;
            if (currentOrder >= newDisplayOrder) {
              toUpdate[journeyRefURL + "/rows/" + row.id + "/displayOrder"] = currentOrder + 1;
            }
          }
        });
      }

      newRow.displayOrder = newDisplayOrder;
      const newRowRef = ref(
        db,
        "userData/" + org.payload + "/journeys/" + params.id + "/rows/"
      );

      // Push the new row first to get its key
      push(newRowRef, newRow).then((snapshot) => {
        // Update displayOrder for affected rows
        if (Object.keys(toUpdate).length) {
          update(ref(db), toUpdate);
        }
      });
    }
  }

  const map = useRef();
  const [selectedItem, setSelectedItem] = useState(false);
  function setSelected(index) {
    setSelectedItem(index);
  }

  function reorder() {
    setJourney(false);

    let toUpdate = {};
    for (let i in map.current.children) {
      if (map.current.childNodes[i] !== undefined) {
        if (
          map.current.childNodes[i].tagName !== undefined &&
          map.current.childNodes[i].dataset["id"]
        ) {
          toUpdate[
            "userData/" +
            org.payload +
            "/journeys/" +
            params.id +
            "/rows/" +
            map.current.childNodes[i].dataset["id"] +
            "/displayOrder"
          ] = i;
        }
      }
    }

    if (Object.keys(toUpdate).length) {
      update(ref(db), toUpdate);
    }
  }
  function deleteCell(e, rowIndex, cellIndex, dir, cellRef, rowRef) {
    let tempJourney = { ...journey };
    tempJourney.rows[rowIndex].columns.splice(cellIndex, 1);

    // Set displayOrder based on array position (not object keys)
    tempJourney.rows[rowIndex].columns.forEach((cell, index) => {
      cell.displayOrder = index;
    });

    let toUpdate = {};
    toUpdate[journeyRefURL + "/rows/" + rowRef + "/columns/"] =
      tempJourney.rows[rowIndex].columns;
    if (Object.keys(toUpdate).length) {
      update(ref(db), toUpdate);
    }
    // Don't update local state - let Firebase listener handle UI update
  }
  function copyAndPaste(e, rowIndex, cellIndex, dir, cellRef, rowRef, val) {
    let tempJourney = { ...journey };

    // Determine source cell index and insertion index
    let sourceCellIndex = cellIndex;
    let newCellIndex = cellIndex;

    if (dir === "right") {
      // Add to the right: copy from current cell, insert after it
      sourceCellIndex = cellIndex;
      newCellIndex = cellIndex + 1;
    } else if (dir === "left") {
      // Add to the left: copy from cell to the left (if exists), insert at current position
      sourceCellIndex = Math.max(0, cellIndex - 1);
      newCellIndex = cellIndex;
    }

    // Ensure we have a valid source cell
    if (!tempJourney.rows[rowIndex] || !tempJourney.rows[rowIndex].columns ||
      !tempJourney.rows[rowIndex].columns[sourceCellIndex]) {
      console.error("Invalid cell index for copyAndPaste");
      return;
    }

    let tempCell = { ...tempJourney.rows[rowIndex].columns[sourceCellIndex] };

    // Generate a unique ID for the new cell to avoid ID collisions
    // Use timestamp + random string to ensure uniqueness
    tempCell.id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    if (val) {
      tempCell.value = val;
    } else {
      tempCell.value = "";
    }


    tempCell.span = 1;

    // Ensure columns is an array
    if (!Array.isArray(tempJourney.rows[rowIndex].columns)) {
      tempJourney.rows[rowIndex].columns = [];
    }

    tempJourney.rows[rowIndex].columns = [
      ...tempJourney.rows[rowIndex].columns.slice(0, newCellIndex),
      tempCell,
      ...tempJourney.rows[rowIndex].columns.slice(newCellIndex),
    ];

    // Set displayOrder based on array position (not object keys)
    // Filter out any undefined/null cells and ensure we have valid objects
    tempJourney.rows[rowIndex].columns = tempJourney.rows[rowIndex].columns.filter(cell => cell != null);
    tempJourney.rows[rowIndex].columns.forEach((cell, index) => {
      if (cell) {
        cell.displayOrder = index;
      }
    });

    let toUpdate = {};
    toUpdate[journeyRefURL + "/rows/" + rowRef + "/columns/"] =
      tempJourney.rows[rowIndex].columns;
    if (Object.keys(toUpdate).length) {
      update(ref(db), toUpdate);
    }
    // Don't update local state - let Firebase listener handle UI update
  }

  const [showRowChooser, setShowRowChooser] = useState(false);
  const [insertAfterRowIndex, setInsertAfterRowIndex] = useState(-1);

  function showRowChooserHandler(insertIndex = -1) {
    setInsertAfterRowIndex(insertIndex);
    setShowRowChooser(true);
  }

  function closeRowChooserHandler() {
    setShowRowChooser(false);
    setInsertAfterRowIndex(-1);
  }

  function handleAddRowClick(rowIndex, direction) {
    let insertIndex = -1;
    if (direction === 'above') {
      // Insert above: insert at position rowIndex (before this row)
      insertIndex = rowIndex - 1;
    } else if (direction === 'below') {
      // Insert below: insert at position rowIndex (after this row)
      insertIndex = rowIndex;
    }
    showRowChooserHandler(insertIndex);
  }

  const [title, setTitle] = useState("");
  
  // Helper function to extract plain text from HTML
  function stripHTML(html) {
    if (!html) return "";
    // Create a temporary DOM element to extract text content
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }
  
  function updateTitleHandler(e) {
    // Extract plain text from HTML to prevent storing HTML markup
    let plainText = stripHTML(e.target.value);
    // Remove newlines and restrict to single line
    plainText = plainText.replace(/\n/g, ' ').replace(/\r/g, '');
    let toUpdate = {};
    toUpdate[journeyRefURL + "/name/"] = plainText;
    if (Object.keys(toUpdate).length) {
      update(ref(db), toUpdate);
    }
    setTitle(plainText);
  }

  useEffect(() => {
    setTitle(journey.name);
  }, [journey.name]);

  useEffect(() => {
    if (journey && journey.video && journey.video.filename && org.payload) {
      getDownloadURL(
        storageRef(storage, org.payload + "/videos/" + journey.video.filename)
      )
        .then((url) => setVideoUrl(url))
        .catch(() => setVideoUrl(null));
    } else {
      setVideoUrl(null);
    }
  }, [journey.video, org.payload]);

  function handleVideoUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file || !journeyRefURL) return;

    setVideoUploading(true);
    const extension = file.name.split(".").pop();
    const uuid = crypto.randomUUID();
    const filename = uuid + "." + extension;
    const videoStorageRef = storageRef(storage, org.payload + "/videos/" + filename);

    uploadBytes(videoStorageRef, file).then(() => {
      update(ref(db), {
        [journeyRefURL + "/video"]: {
          filename,
          originalName: file.name,
          uploadedBy: user.uid,
          uploadedAt: Date.now(),
        },
      });
      setVideoUploading(false);
    }).catch(() => {
      setVideoUploading(false);
    });

    e.target.value = "";
  }

  function handleRemoveVideo() {
    if (!journey.video || !journey.video.filename || !journeyRefURL) return;

    const videoStorageRef = storageRef(
      storage,
      org.payload + "/videos/" + journey.video.filename
    );
    deleteObject(videoStorageRef).catch(() => {});
    update(ref(db), { [journeyRefURL + "/video"]: null });
    setVideoUrl(null);
    closeJourneyOptionsHandler();
  }

  function triggerVideoUpload() {
    closeJourneyOptionsHandler();
    setTimeout(() => videoInputRef.current?.click(), 100);
  }

  // function onChangeHandler(e, columnId) {
  //   update(ref(db), {
  //     [props.journeyRefURL +
  //     "/rows/" +
  //     props.row.id +
  //     "/columns/" +
  //     columnId +
  //     "/value"]: sanitizeHTML(e.target.value),
  //   });
  //   setCellVal(e.target.value);
  // }

  function setFromTemplate(template) {
    if (template === "blank") {
    } else {
      var updates = {};
      template.map((item) => {
        var newPostKey = push(
          ref(
            db,
            "userData/" + org.payload + "/journeys/" + params.id + "/rows/"
          ),
          []
        ).key;
        updates[
          "userData/" +
          org.payload +
          "/journeys/" +
          params.id +
          "/rows/" +
          newPostKey
        ] = item;
      });
      update(ref(db), updates);
    }
  }

  const [isDragging, setIsDragging] = useState(false);
  function isDraggingHandler(state) {
    setIsDragging(state);
  }

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showCellComments, setShowCellComments] = useState(false);
  const [selectedCellForComments, setSelectedCellForComments] = useState({ rowId: null, cellId: null });
  const [cellCommentCounts, setCellCommentCounts] = useState({});
  const [cellUnreadCounts, setCellUnreadCounts] = useState({});
  const { getUnreadCountForCell, markCellNotificationsAsRead } = useNotifications();

  async function showVersionHistoryHandler() {
    // If currently in edit mode, exit edit mode first so auto-versioning and
    // lock release logic can run before entering time machine.
    if (editMode) {
      await editModeHandler();
    }

    setShowJourneyOptions(false);

    setShowVersionHistory(true);
    setShowNotes(false);
    // Enter time machine mode as soon as the version history sidebar is opened
    setTimeMachineMode(true);
  }

  function hideVersions() {
    setShowVersionHistory(false);
    setShowNotes(false);
    // Exit time machine mode when closing the history/notes sidebar
    exitTimeMachine();
  }

  function showNotesHandler() {
    setShowNotes(true);
    setShowVersionHistory(false);
  }

  const [notes, setNotes] = useState("");
  function newNoteChangeHandler(e) {
    setNotes(e.target.value);
  }
  function addNoteHandler() {
    const notesRef = ref(
      db,
      "userData/" + org.payload + "/journeys/" + params.id + "/notes/"
    );
    push(notesRef, {
      value: notes,
      by: user.displayName,
      timestamp: { ".sv": "timestamp" },
    });
    setNotes("");
  }

  const [showAddSus, setShowAddSus] = useState(false);
  function addSus() {
    setShowAddSus(true);
    setShowJourneyOptions(false);
  }
  function hideSus() {
    setShowAddSus(false);
  }

  const [showLinkPersonas, setShowLinkPersonas] = useState(false);
  function showLinkPersonasHandler() {
    setShowLinkPersonas(true);
    setShowJourneyOptions(false);
  }
  function hideLinkPersonas() {
    setShowLinkPersonas(false);
  }

  const [showJourneyOwners, setShowJourneyOwners] = useState(false);
  function showJourneyOwnersHandler() {
    setShowJourneyOwners(true);
    setShowJourneyOptions(false);
  }
  function hideJourneyOwners() {
    setShowJourneyOwners(false);
  }

  const [journeyOwners, setJourneyOwners] = useState([]);

  function openCellComments(rowId, cellId) {
    setSelectedCellForComments({ rowId, cellId });
    setShowCellComments(true);
    // Mark notifications as read for this cell
    if (params.id) {
      markCellNotificationsAsRead(params.id, rowId, cellId);
    }
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("commentRowId", rowId);
    newSearchParams.set("commentCellId", cellId);
    setSearchParams(newSearchParams, { replace: true });
  }

  function closeCellComments() {
    setShowCellComments(false);
    setSelectedCellForComments({ rowId: null, cellId: null });
    // Remove URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("commentRowId");
    newSearchParams.delete("commentCellId");
    setSearchParams(newSearchParams, { replace: true });
  }

  // Check URL params on mount and when they change
  useEffect(() => {
    const commentRowId = searchParams.get("commentRowId");
    const commentCellId = searchParams.get("commentCellId");
    
    if (commentRowId && commentCellId && journeyRefURL && params.id) {
      setSelectedCellForComments({ rowId: commentRowId, cellId: commentCellId });
      setShowCellComments(true);
      // Mark notifications as read when opening via URL
      markCellNotificationsAsRead(params.id, commentRowId, commentCellId);
    }
  }, [searchParams, journeyRefURL, params.id, markCellNotificationsAsRead]);

  // Update unread counts for cells
  useEffect(() => {
    if (!journey || !journey.rows || !params.id) {
      return;
    }

    const unreadCounts = {};
    journey.rows.forEach((row) => {
      if (row.columns && Array.isArray(row.columns)) {
        row.columns.forEach((cell, cellIndex) => {
          const unreadCount = getUnreadCountForCell(params.id, row.id, cellIndex);
          const key = `${row.id}_${cellIndex}`;
          unreadCounts[key] = unreadCount;
        });
      }
    });
    setCellUnreadCounts(unreadCounts);
  }, [journey, params.id, getUnreadCountForCell]);

  function archiveJourney() {
    if (window.confirm("Are you sure you want to archive this journey? You can view it in the Archive tab.")) {
      const toUpdate = {};
      toUpdate[journeyRefURL + "/archived"] = true;
      update(ref(db), toUpdate).then(() => {
        setShowJourneyOptions(false);
        navigate("/journeys");
      });
    }
  }

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const [file, setFile] = useState("");
  const [error, setError] = useState("");
  const [SUS, setSUS] = useState([]);

  function inputFileChangeHandler(tfile) {
    setFile(tfile);
  }

  function handleParse() {
    // If user clicks the parse button without
    // a file we show a error
    if (!file) return setError("Enter a valid file");

    // Initialize a reader which allows user
    // to read any file or blob.
    const reader = new FileReader();
    // Event listener on reader when the file
    // loads, we parse it and set the data.
    reader.onload = async ({ target }) => {
      const csv = Papa.parse(target.result, { header: true });
      const parsedData = csv?.data;
      // {console.log(parsedData)}
      // const columns = Object.keys(parsedData[0]);
      // setData(parsedData);
      let totalSUS = [];
      let completedUsers = [];
      let userResponses = [];
      let month = 0;
      parsedData.map((user, index) => {
        let uOdd = 0;
        let uEven = 0;
        let uSUS = 0;
        if (user.completedAt && user.completedAt !== "Not finished") {
          // console.log("val",Number(user.completedAtTimestamp))
          let date = new Date(parseInt(Number(user.completedAtTimestamp)));

          // let date = Date.parse(user.completedAt);

          // if (JSON.parse(user.meta).completedAt && JSON.parse(user.meta).completedAt !== "Not finished") {
          //   let date = new Date(JSON.parse(user.meta).completedAt);
          console.log("data", date.getMonth());
          let month = date.getMonth();
          let datePrint = month + 1 + "-01-" + date.getFullYear();

          // console.log(date.getDate() + " / " + (date.getMonth() + 1) + " / " + date.getFullYear())
          if (typeof completedUsers[month] === "undefined") {
            completedUsers[month] = 0;
          }
          completedUsers[month] += 1;
          uOdd += parseInt(
            user[
            "[2] [scale] 1. I think that I would like to use BetKing frequently."
            ]
          );
          uOdd += parseInt(
            user["[4] [scale] 3. I thought BetKing was easy to use."]
          );
          uOdd += parseInt(
            user[
            "[6] [scale] 5. I found the various functions in BetKing were well integrated."
            ]
          );
          uOdd += parseInt(
            user[
            "[8] [scale] 7. I would imagine that most people would learn to use BetKing very quickly."
            ]
          );
          uOdd += parseInt(
            user["[10] [scale] 9. I felt very confident using BetKing."]
          );

          uEven += parseInt(
            user["[3] [scale] 2. I found BetKing unnecessarily complex."]
          );
          uEven += parseInt(
            user[
            "[5] [scale] 4. I think that I would need the support of a technical person to be able to use BetKing."
            ]
          );
          uEven += parseInt(
            user[
            "[7] [scale] 6. I thought there was too much inconsistency in BetKing."
            ]
          );
          uEven += parseInt(
            user["[9] [scale] 8. I found BetKing very awkward to use."]
          );
          uEven += parseInt(
            user[
            "[11] [scale] 10. I needed to learn a lot of things before I could get going with BetKing."
            ]
          );

          if (!isNaN(uOdd) && !isNaN(uEven)) {
            uOdd = uOdd - 5;
            uEven = 25 - uEven;
            uSUS = (uOdd + uEven) * 2.5;
            userResponses.push({
              sus: uSUS,
              data: user[
                "[12] [input] What can we do to improve your experience?"
              ],
            });
            // console.log("uOdd", uOdd);
            // console.log("uEven", uEven);
            // console.log("uSUS", uSUS);
            // console.log("user", user);
            if (typeof totalSUS[month] === "undefined") {
              totalSUS[month] = { score: 0 };
            }
            totalSUS[month].score = totalSUS[month].score + uSUS;
            totalSUS[month].datePrint = datePrint;
            totalSUS[month].year = date.getFullYear();
          }
        }
      });

      // console.log(userResponses);

      let prompt = {
        prompt:
          "Do not include any explanations, only provide a RFC8259 compliant JSON response following this format without deviation. Using the following data by adding a cell 'category' that categorizes the feedback in cell 'data'.  Ignore false, blank or null entries: " +
          JSON.stringify(userResponses),
      };

      const options = {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(prompt),
      };
      // console.log(options);
      // fetch("http://localhost:8080/chat", options)
      //   .then((response) => response.json())
      //   .then((data) => handleDataResponse(data));

      //   function handleDataResponse(data) {
      //     console.log(data);
      //     console.log(JSON.parse(data.message.content));
      //   }

      totalSUS.map((m, index) => {
        let tempAdjective = "";
        let tempScore = (m.score / completedUsers[index]).toFixed(1);

        if (tempScore > 0 && tempScore <= 25) {
          tempAdjective = "Worst";
        }
        if (tempScore > 25 && tempScore <= 51.6) {
          tempAdjective = "Poor";
        }
        if (tempScore > 51.6 && tempScore <= 71) {
          tempAdjective = "OK";
        }
        if (tempScore > 71 && tempScore <= 80.7) {
          tempAdjective = "Good";
        }
        if (tempScore > 80.7 && tempScore <= 84) {
          tempAdjective = "Excellent";
        }
        if (tempScore > 84 && tempScore <= 100) {
          tempAdjective = "Best";
        }

        totalSUS[index] = {
          datePrint: m.datePrint,
          year: m.year,
          score: tempScore,
          users: completedUsers[index],
          adjective: tempAdjective,
        };
      });
      setSUS(totalSUS);
    };
    reader.readAsText(file);
  }

  function saveSUS(date, score, adjective, users) {
    let toUpdate = {};
    toUpdate[journeyRefURL + "/sus/" + date] = {
      adjective: adjective,
      month: date,
      value: score,
      users: users,
    };
    if (Object.keys(toUpdate).length) {
      update(ref(db), toUpdate);
    }
    setShowAddSus(false);
  }

  const [showSUSDetails, setShowSUSDetails] = useState(false);

  function showSUSDetailsHandler() {
    setShowSUSDetails(true);
  }
  function hideSUSDetailsHandler() {
    setShowSUSDetails(false);
  }

  const [SUScores, setSUScores] = useState();
  const [suss, setSuss] = useState([]);

  useEffect(() => {
    let temp = [];
    for (let i in journey.sus) {
      const date = new Date(journey.sus[i].month);
      journey.sus[i].monthString = date.toLocaleString("default", {
        month: "short",
      });
      temp.push(journey.sus[i]);
    }
    var sortedArray = temp.sort(callback);
    setSuss(sortedArray);
  }, [journey.sus]);

  function callback(a, b) {
    var aDate = a.month.split("-");
    var bDate = b.month.split("-");
    var newADate = new Date(aDate[2], aDate[1] - 1, aDate[0]);
    var newBDate = new Date(bDate[2], bDate[1] - 1, bDate[0]);
    if (newADate < newBDate) return -1;
    if (newADate > newBDate) return 1;
    return 0;
  }

  // Load comment counts for all cells
  useEffect(() => {
    if (!journeyRefURL || !journey || !journey.rows || timeMachineMode) {
      return;
    }

    const counts = {};
    const unsubscribeFunctions = [];

    journey.rows.forEach((row) => {
      if (row.columns && Array.isArray(row.columns)) {
        row.columns.forEach((cell, cellIndex) => {
          // Use cellIndex for Firebase path since columns are stored as array
          const commentsRef = ref(
            db,
            `${journeyRefURL}/rows/${row.id}/columns/${cellIndex}/comments`
          );
          
          const unsubscribe = onValue(commentsRef, (snapshot) => {
            const commentsData = snapshot.val();
            const count = commentsData ? Object.keys(commentsData).length : 0;
            const key = `${row.id}_${cellIndex}`;
            setCellCommentCounts((prev) => ({
              ...prev,
              [key]: count,
            }));
          });
          
          unsubscribeFunctions.push(unsubscribe);
        });
      }
    });

    return () => {
      unsubscribeFunctions.forEach((unsub) => unsub());
    };
  }, [journeyRefURL, journey, timeMachineMode]);

  // Use archived version data when in time machine mode
  const displayJourney = timeMachineMode && selectedVersionData ? selectedVersionData : journey;
  const displayTitle = timeMachineMode && selectedVersionData ? selectedVersionData.name : title;
  const displayLabels = timeMachineMode && selectedVersionData ? (selectedVersionData.labels || {}) : journey.labels;
  const displayCanEdit = timeMachineMode ? false : canEdit; // Disable edit in time machine mode

  return (
    <div className={`${style.journey} ${timeMachineMode ? style.timeMachineMode : ''}`}>
      <TitleBar
          title={displayTitle ? displayTitle : "Fetching"}
          actions={actions}
          addLabels={true}
          onAddLabel={onAddLabel}
          onRemoveLabel={onRemoveLabel}
          labels={displayLabels}
          editable={displayCanEdit ? editMode : false}
          updateTitle={updateTitleHandler}
          darkMode={timeMachineMode}
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Journeys", url: "/journeys" },
        ]}
        users={users}
        owners={journeyOwners}
        sus={journey.sus}
        showSUSDetails={showSUSDetailsHandler}
        journeyScore={journeyScore}
        headerExtra={
          journey.video ? (
            <span
              className={`${style.videoIcon} ${videoUploading ? style.videoUploading : ""}`}
              title={journey.video.originalName || "Watch journey recording"}
              onClick={() => setShowVideoPlayer(true)}
            >
              <Icon>play_circle</Icon>
            </span>
          ) : videoUploading ? (
            <span className={`${style.videoIcon} ${style.videoUploading}`} title="Uploading video...">
              <Icon>hourglass_empty</Icon>
            </span>
          ) : null
        }
      />
      {isLockedByOtherUser && !editMode && (
        <div className={style.editLockBanner}>
          <Icon>lock</Icon>
          <span>
            This journey is being edited by <strong>{editLock?.userName || 'another user'}</strong>. 
            You can view but cannot make changes until they finish editing.
          </span>
        </div>
      )}
      <div
        ref={map}
        className={`${style.map} ${isDragging && style.isDragging}`}
      >
        <div
          className={`${style.versionHistory} ${(showVersionHistory || showNotes) && style.show
            } ${timeMachineMode ? style.timeMachineDark : ''}`}
        >
          <div className={style.header}>
            <span></span>
            <Icon onClick={hideVersions}>close</Icon>
          </div>

          <div
            className={`${style.sidebarContent} ${style.notes} ${showNotes && style.selected
              }`}
          >
            <div className={style.list}>
              {journey.notes &&
                journey.notes.map((note, index) => (
                  <div key={index}>
                    <div>{note.timestamp}</div>
                    <div className={style.value}>{note.value}</div>
                    <div>{note.by}</div>
                  </div>
                ))}
            </div>
            <div>
              {canEdit && editMode ? (
                <div className={style.addNote}>
                  <ContentEditable
                    inputMode="text"
                    html={notes}
                    placeholder="text here"
                    onChange={newNoteChangeHandler}
                  />
                  <Button preIcon="add" onClick={addNoteHandler}>
                    Add Event
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
          <div
            className={`${style.sidebarContent} ${showVersionHistory && style.selected
              }`}
          >
            {timeMachineMode && (
              <div className={style.versionNavigation}>
                {/* <div className={style.versionInfo}>
                  <div className={style.versionInfoTitle}>Viewing Historical Version</div>
                  {selectedVersionId && journey.archive && (() => {
                    const currentVersion = journey.archive.find(v => v.id === selectedVersionId);
                    return currentVersion ? (
                      <>
                        <div className={style.versionInfoMeta}>
                          <div>By: {currentVersion.by}</div>
                          <div>{currentVersion.timestamp}</div>
                        </div>
                        <div className={style.versionInfoPosition}>
                          Version {journey.archive.findIndex(v => v.id === selectedVersionId) + 1} of {journey.archive.length}
                        </div>
                      </>
                    ) : null;
                  })()}
                </div> */}
                <div className={style.versionControls}>
                  <Button
                    preIcon="chevron_left"
                    onClick={navigateToPreviousVersion}
                    disabled={!selectedVersionId || !journey.archive}
                    variants={[]}
                  >
                    Previous
                  </Button>
                  <Button
                    trailingIcon="chevron_right"
                    onClick={navigateToNextVersion}
                    disabled={!selectedVersionId || !journey.archive || false}
                    variants={[]}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
            {journey.archive && journey.archive.length > 0 ? (
              <>
                {/* Current Version Item */}
                <div
                  className={`${style.versionItem} ${timeMachineMode && !selectedVersionId ? style.selected : ''}`}
                  onClick={() => {
                    // Show the current version while staying in time machine mode
                    setSelectedVersionId(null);
                    setSelectedVersionData(null);
                  }}
                >
                  <div>
                    {user && user.displayName && <div>{user.displayName}</div>}
                  </div>
                  <div><div className={style.archiveDate}>Current Version</div></div>
                </div>
                {/* Historical Versions */}
                {journey.archive.map((ver, index) => (
                  <div
                    key={index}
                    className={`${style.versionItem} ${selectedVersionId === ver.id ? style.selected : ''}`}
                    onClick={() => loadArchivedVersion(ver.id)}
                  >
                    <div>
                      <div>{ver.by}</div>
                    </div>
                    <div>{ver.timestamp}</div>
                  </div>
                ))}
              </>
            ) : (
              <div className={style.emptyVersions}>
                <p>No historical versions yet.</p>
                <p>Versions will be created automatically when you make changes to the journey.</p>
              </div>
            )}
          </div>
        </div>
        {(!displayJourney || !displayJourney.rows || displayJourney.rows.length === 0) && editMode && !timeMachineMode ? (
          <div className={style.templates}>
            <div className={style.templatesTitle}>
              <h2>Choose a template to get started</h2>
              Get off the ground running by choosing a template to start from.
              Once picked, you'll be able to add/remove lanes and columns to
              your needs
            </div>
            <div>
              <Card
                title="Blank"
                text="Start with a blank canvas"
                ctas={[
                  {
                    name: "Start",
                    onClick: () => setFromTemplate("blank"),
                  },
                ]}
                variants={["white"]}
              />

              <Card
                title="Basic"
                text="A simple text lane and image lane"
                ctas={[
                  {
                    name: "Start",
                    onClick: () =>
                      setFromTemplate([
                        {
                          type: "text",
                          value: "Description",
                          columns: {
                            0: {
                              value: "Text",
                            },
                            1: {
                              value: "Text",
                            },
                            2: {
                              value: "Text",
                            },
                          },
                        },
                        {
                          type: "picture",
                          value: "Visual",
                          columns: {
                            0: {
                              value: "",
                            },
                            1: {
                              value: "",
                            },
                            2: {
                              value: "",
                            },
                          },
                        },
                      ]),
                  },
                ]}
                variants={["white"]}
              />
            </div>
            <div>
              <Card
                title="Funnel"
                text="Four lanes including a conversion funnel lane"
                ctas={[
                  {
                    name: "Start",
                    onClick: () =>
                      setFromTemplate([
                        {
                          type: "phases",
                          value: "Lifecycle",
                          columns: {
                            0: {
                              value: "Text",
                            },
                            1: {
                              value: "Text",
                            },
                            2: {
                              value: "Text",
                            },
                          },
                        },
                        {
                          type: "text",
                          value: "Description",
                          columns: {
                            0: {
                              value: "Text",
                            },
                            1: {
                              value: "Text",
                            },
                            2: {
                              value: "Text",
                            },
                          },
                        },
                        {
                          type: "picture",
                          value: "Visual",
                          columns: {
                            0: {
                              value: "",
                            },
                            1: {
                              value: "",
                            },
                            2: {
                              value: "",
                            },
                          },
                        },
                        {
                          type: "funnel",
                          value: "Conversion",
                          columns: {
                            0: {
                              value: 100,
                            },
                            1: {
                              value: 50,
                            },
                            2: {
                              value: 25,
                            },
                          },
                        },
                      ]),
                  },
                ]}
                variants={["white"]}
              />

              <Card
                title="All Lanes"
                text="All of our lanes, reorder, remove and/or add to your needs"
                ctas={[
                  {
                    name: "Start",
                    onClick: () =>
                      setFromTemplate([
                        {
                          type: "phases",
                          value: "Lifecycle",
                          columns: {
                            0: {
                              value: "Text",
                            },
                            1: {
                              value: "Text",
                            },
                            2: {
                              value: "Text",
                            },
                          },
                        },
                        {
                          type: "text",
                          value: "Description",
                          columns: {
                            0: {
                              value: "Text",
                            },
                            1: {
                              value: "Text",
                            },
                            2: {
                              value: "Text",
                            },
                          },
                        },
                        {
                          type: "time",
                          value: "Time",
                          columns: {
                            0: {
                              value: 25,
                            },
                            1: {
                              value: 25,
                            },
                            2: {
                              value: 25,
                            },
                          },
                        },
                        {
                          type: "categories",
                          value: "Category",
                          columns: {
                            0: {
                              value: "Text",
                            },
                            1: {
                              value: "Text",
                            },
                            2: {
                              value: "Text",
                            },
                          },
                        },
                        {
                          type: "picture",
                          value: "Visual",
                          columns: {
                            0: {
                              value: "",
                            },
                            1: {
                              value: "",
                            },
                            2: {
                              value: "",
                            },
                          },
                        },
                        {
                          type: "funnel",
                          value: "Conversion",
                          columns: {
                            0: {
                              value: 100,
                            },
                            1: {
                              value: 50,
                            },
                            2: {
                              value: 25,
                            },
                          },
                        },
                        {
                          type: "emotion",
                          value: "Emotion",
                          columns: {
                            0: {
                              value: 3,
                            },
                            1: {
                              value: 3,
                            },
                            2: {
                              value: 3,
                            },
                          },
                        },
                        {
                          type: "barchart",
                          value: "Bar Chart",
                          columns: {
                            0: {
                              value: 50,
                            },
                            1: {
                              value: 50,
                            },
                            2: {
                              value: 50,
                            },
                          },
                        },
                        {
                          type: "scoring",
                          value: "Scoring",
                          columns: {
                            0: {
                              value: "",
                            },
                            1: {
                              value: "",
                            },
                            2: {
                              value: "",
                            },
                          },
                        },
                      ]),
                  },
                ]}
                variants={["white"]}
              />
            </div>
          </div>
        ) : (!displayJourney || !displayJourney.rows || displayJourney.rows.length === 0) ? (
          <div className={style.noData}>
            <span>
              No information has been added to this journey yet{" "}
              {canEdit ? (
                <>
                  <br />
                  Go to{" "}
                  <span className="link" onClick={editModeHandler}>
                    edit mode
                  </span>{" "}
                  to add information{" "}
                </>
              ) : null}
            </span>
          </div>
        ) : null}
        {displayJourney && displayJourney.rows
          ? displayJourney.rows.map((row, index) => (
            <Row
              key={index}
              name={row.value}
              data={row}
              type={row.type}
              index={index}
              rowIndex={index}
              editable={displayCanEdit ? editMode : false}
              rowId={row.id}
              journeyRefURL={journeyRefURL}
              setSelected={setSelected}
              selectedItem={selectedItem}
              map={map}
              reorder={reorder}
              copyAndPaste={copyAndPaste}
              addRow={showRowChooserHandler}
              onAddRowClick={handleAddRowClick}
              isDraggingHandler={isDraggingHandler}
              deleteCell={deleteCell}
              maxColumns={maxColumns}
              timeMachineMode={timeMachineMode}
              onOpenComments={!timeMachineMode ? openCellComments : null}
              cellCommentCounts={cellCommentCounts}
              cellUnreadCounts={cellUnreadCounts}
            />
          ))
          : null}
        <Row
          blank={true}
          index={displayJourney && displayJourney.rows ? displayJourney.rows.length : 0}
          selectedItem={selectedItem}
          map={map}
          reorder={reorder}
        >

        </Row>
      </div>
      <Dropdown
        show={showJourneyOptions}
        onClose={closeJourneyOptionsHandler}
        style={journeyOptionsStyle}
      >
        <ul className={style.journeyOptions}>
          
          {canEdit && editMode ? (
            <>
              <li>
                <Button
                  preIcon="person"
                  onClick={showLinkPersonasHandler}
                  variants={["text"]}
                >
                  Link Personas
                </Button>
              </li>
              <li>
                <Button
                  preIcon="person_add"
                  onClick={showJourneyOwnersHandler}
                  variants={["text"]}
                >
                  Journey Owners
                </Button>
              </li>
              <li>
                <Button
                  preIcon={journey.video ? "video_settings" : "video_call"}
                  onClick={triggerVideoUpload}
                  variants={["text"]}
                >
                  {journey.video ? "Replace Video" : "Attach Video"}
                </Button>
              </li>
              {journey.video ? (
                <li>
                  <Button
                    preIcon="delete"
                    onClick={handleRemoveVideo}
                    variants={["text"]}
                  >
                    Remove Video
                  </Button>
                </li>
              ) : null}
            </>
          ) : <li>
          <Button
            preIcon="history"
            onClick={showVersionHistoryHandler}
            variants={["text"]}
          >
            Time Machine
          </Button>
        </li>}
          <li>
            <Button
              preIcon="download"
              onClick={showExportDialogHandler}
              variants={["text"]}
            >
              Export Journey
            </Button>
          </li>
          {journey && journey.brandIds && journey.brandIds.length > 0 && brands.length > 0 && (
            <li className={style.brandInfo}>
              <div className={style.brandLabel}>Brands:</div>
              <div className={style.brandList}>
                {journey.brandIds
                  .map(brandId => brands.find(b => b.id === brandId))
                  .filter(b => b !== undefined)
                  .map(brand => (
                    <span key={brand.id} className={style.brandTag}>
                      {brand.name}
                    </span>
                  ))}
              </div>
            </li>
          )}
          {canEdit && editMode ? (
            <>
              <li>
                <Button preIcon="content_copy" variants={["text"]}>
                  Duplicate
                </Button>
              </li>
              {/* <li>
                <Button
                  preIcon="health_and_safety"
                  variants={["text"]}
                  onClick={addSus}
                >
                  Add SUS
                </Button>
              </li>
              <li>
                <Button
                  preIcon="archive"
                  variants={["text"]}
                  onClick={archiveJourney}
                >
                  Archive
                </Button>
              </li> */}
            </>
          ) : null}
        </ul>
      </Dropdown>
      <Dialog
        show={showRowChooser}
        title="Choose a Lane Type"
        variants={["large"]}
        onClose={closeRowChooserHandler}
      >
        <div className={style.rowChooser}>
          <div>
            <div><img src={require("./images/phases.webp")} alt="Phases" /></div>
            <div><h3>Phases</h3>Show the various phases of your journeys using different labels and colors</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "phases",
                value: "Row Title",
                columns: {
                  0: {
                    value: "Text",
                    style: "style1",
                  },
                  1: {
                    value: "Text",
                    style: "style2",
                  },
                  2: {
                    value: "Text",
                    style: "style3",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>

          <div>
            <div><img src={require("./images/categories.webp")} alt="Categories" /></div>
            <div><h3>Categories</h3>Text that alternates background colors</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "categories",
                value: "Row Title",
                columns: {
                  0: {
                    value: "Text",
                  },
                  1: {
                    value: "Text",
                  },
                  2: {
                    value: "Text",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>

          <div>
            <div><img src={require("./images/picture.webp")} alt="Picture" /></div>
            <div><h3>Images</h3> Insert images to depict your retail experience or add a screenshot from a digital journey</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "picture",
                value: "Row Title",
                columns: {
                  0: {
                    value: "",
                  },
                  1: {
                    value: "",
                  },
                  2: {
                    value: "",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/funnel.webp")} alt="Funnel" /></div>
            <div><h3>Funnel</h3> Show how customers drop off at each step of your journey</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "funnel",
                value: "Row Title",
                columns: {
                  0: {
                    value: 100,
                  },
                  1: {
                    value: 75,
                  },
                  2: {
                    value: 50,
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>

          <div>
            <div><img src={require("./images/emotions.webp")} alt="Emotions" /></div>
            <div><h3>Emotions</h3> Depict the emotional rollwe coaster your uses might be experiencing</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "emotion",
                value: "Row Title",
                columns: {
                  0: {
                    value: 3,
                  },
                  1: {
                    value: 3,
                  },
                  2: {
                    value: 3,
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/text.webp")} alt="Text" /></div>
            <div><h3>Text</h3> Simple text box for comments and ideas</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "text",
                value: "Row Title",
                columns: {
                  0: {
                    value: "Text",
                  },
                  1: {
                    value: "Text",
                  },
                  2: {
                    value: "Text",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/text.webp")} alt="Quote" /></div>
            <div><h3>Quote</h3> Display text in a speech bubble format</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "quote",
                value: "Quote",
                columns: {
                  0: {
                    value: "Text",
                  },
                  1: {
                    value: "Text",
                  },
                  2: {
                    value: "Text",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/time.webp")} alt="Time" /></div>
            <div><h3>Time</h3> A simple digital clock</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "time",
                value: "Time",
                columns: {
                  0: {
                    value: "90",
                  },
                  1: {
                    value: "90",
                  },
                  2: {
                    value: "90",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/barchart.webp")} alt="Bar Chart" /></div>
            <div><h3>Bar Chart</h3> Add a bar to each of your columns to represent occurances</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "barchart",
                value: "Bars",
                columns: {
                  0: {
                    value: "90",
                  },
                  1: {
                    value: "90",
                  },
                  2: {
                    value: "90",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
         
          <div>
            <div><img src={require("./images/stepNumber.webp")} alt="Step" /></div>
            <div><h3>Step</h3> Displays a number for each step of your journey</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "stepNumber",
                value: "Steps",
                columns: {},
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/scoring.webp")} alt="Scoring" /></div>
            <div><h3>Scoring</h3> Evaluate journey steps across 6 UX dimensions with scores from multiple designers</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "scoring",
                value: "Scoring",
                columns: {
                  0: {
                    value: "",
                  },
                  1: {
                    value: "",
                  },
                  2: {
                    value: "",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
          
          <div>
            <div><img src={require("./images/figma-pic.webp")} alt="Figma" /></div>
            <div><h3>Figma</h3> Embed Figma frames and designs to visualize your journey</div>
            <div><Button variants={["text"]} preIcon="add" onClick={() => {
              addRow({
                type: "figma",
                value: "Figma",
                columns: {
                  0: {
                    value: "",
                  },
                  1: {
                    value: "",
                  },
                  2: {
                    value: "",
                  },
                },
              }, insertAfterRowIndex);
              closeRowChooserHandler();
            }}></Button></div>
          </div>
        </div>
      </Dialog>

      <Dialog show={showAddSus} onClose={hideSus} title="Add SUS">
        <div className={style.months}>
          {SUS.map((data, index) => (
            <div key="index" className={style.month}>
              {months[index]} {data.year}
              <div className={`${style.SUS} ${style[data.adjective]}`}>
                <div className={style.adjective}>{data.adjective}</div>
                {data.score}
                <div className={style.numberOfUsers}>
                  {" "}
                  <Icon>group</Icon> {data.users}
                </div>
              </div>
              <Button
                variants={["text"]}
                onClick={() =>
                  saveSUS(
                    data.datePrint,
                    data.score,
                    data.adjective,
                    data.users
                  )
                }
              >
                Save
              </Button>
            </div>
          ))}
        </div>
        <InputFile
          label="Choose File"
          placeholder=" "
          onChange={inputFileChangeHandler}
        />
        <Button variants={["text"]} onClick={handleParse}>
          Calculate SUS
        </Button>
      </Dialog>

      {suss.length ? (
        <Dialog
          show={showSUSDetails}
          onClose={hideSUSDetailsHandler}
          title="System Usability Scale"
          variants={["large"]}
        >
          <div className={style.susHistory}>
            {suss.map((data, i) => (
              <span
                key={i}
                style={{ height: data.value + "%" }}
                className={style[data.adjective]}
                data-month={data.monthString}
                data-value={
                  "Score:\u00a0" +
                  data.value +
                  " Users:\u00a0" +
                  data.users +
                  " " +
                  (suss[i - 1]
                    ? ((data.value - suss[i - 1].value) / suss[i - 1].value) *
                    100
                    : 0
                  ).toFixed(2) +
                  "%"
                }
              ></span>
            ))}
          </div>
          <div className={style.key}>
            <div><span className={style.Worst}></span> Worst<br /> (0 - 25)</div>
            <div><span className={style.Poor}></span> Poor<br /> (25.1 - 51.6)</div>
            <div><span className={style.OK}></span> OK<br /> (51.7 - 71)</div>
            <div><span className={style.Good}></span> Good<br /> (71.1 - 80.7)</div>
            <div><span className={style.Excellent}></span> Excellent<br /> (80.8 - 84)</div>
            <div><span className={style.Best}></span> Best<br /> (84.1 - 100)</div>
          </div>
        </Dialog>
      ) : null}

      <LinkPersonasDialog
        show={showLinkPersonas}
        onClose={hideLinkPersonas}
        journeyId={params.id}
        journeyName={journey.name}
        orgId={org.payload}
      />

      <JourneyOwnersDialog
        show={showJourneyOwners}
        onClose={hideJourneyOwners}
        journeyId={params.id}
        orgId={org.payload}
      />

      <CellCommentsSidebar
        show={showCellComments}
        onClose={closeCellComments}
        journeyRefURL={journeyRefURL}
        rowId={selectedCellForComments.rowId}
        cellId={selectedCellForComments.cellId}
        user={user}
        journeyId={params.id}
        journeyName={journey?.name || ""}
      />

      <Dialog
        show={showExportDialog}
        onClose={closeExportDialogHandler}
        title="Export Journey"
      >
        <div className={style.exportDialog}>
          {displayJourney && displayJourney.rows && displayJourney.rows.length > 0 ? (
            <div className={style.rowList}>
              {displayJourney.rows.map((row) => (
                <div key={row.id} className={style.rowListItem}>
                  <Checkbox
                    checked={selectedRowsForExport.has(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    value={row.value || "Untitled Row"}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div>No rows available to export.</div>
          )}
          <div className={style.pdfFriendlyOptions}>
            <Checkbox
              checked={pdfFriendly}
              onChange={() => setPdfFriendly((prev) => !prev)}
              value="PDF friendly (A4)"
            />
            {pdfFriendly && (
              <div className={style.pdfMaxColumnsInput}>
                <Input
                  type="number"
                  label="Max columns per row"
                  value={pdfMaxColumns}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 2) {
                      setPdfMaxColumns(Math.min(val, maxColumns || 50));
                    }
                  }}
                />
              </div>
            )}
            <Checkbox
              checked={hideRowTitles}
              onChange={() => setHideRowTitles((prev) => !prev)}
              value="Hide row titles"
            />
          </div>
          <div className={style.exportDialogActions}>
            <Button variants={["text"]} onClick={closeExportDialogHandler}>
              Cancel
            </Button>
            <Button variants={["primary"]} onClick={handleExportConfirm}>
              Export
            </Button>
          </div>
        </div>
      </Dialog>

      <VideoPlayerDialog
        show={showVideoPlayer}
        onClose={() => setShowVideoPlayer(false)}
        videoUrl={videoUrl}
        videoName={journey.video?.originalName}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        style={{ display: "none" }}
        onChange={handleVideoUpload}
      />
    </div>
  );
};

export default Journey;


